// Task 7 — team roster panel, toggled under a team's row in
// TeamsAdminPage. Lists current members (users whose teamId matches this
// team, from the tenant users list — the /users route's select was
// extended with teamId in this task) plus an "Add member" picker of
// everyone else. Picking a user who already belongs to another team
// prompts a transfer confirmation before calling assignMember — the
// endpoint just reassigns the FK, so a "transfer" and an "assign" are the
// same call.
//
// The users list is fetched once by TeamsAdminPage and passed down here as
// a prop (/simplify fix G) — this panel used to fetch it independently,
// which meant three GET /users round trips (page, this panel, TeamDialog)
// any time an admin opened the page and expanded a row.
import { useMemo, useState } from "react";
import { showApiError } from "@/lib/error-toast";
import { teamsApi, type TeamRecord, type TenantUser } from "../api/client";
import styles from "../teams.module.css";

export interface MembersPanelProps {
  team: TeamRecord;
  /** Full team list — used to name a user's current team in the transfer confirm. */
  teams: TeamRecord[];
  /** Tenant users — roster/candidates source, fetched once by the page. */
  users: TenantUser[];
  /** Called after a successful assign/remove so the parent can refetch teams + users. */
  onChanged: () => void;
}

export function MembersPanel({ team, teams, users, onChanged }: MembersPanelProps) {
  const [selectedUserId, setSelectedUserId] = useState("");
  const [busy, setBusy] = useState(false);

  const teamNameById = useMemo(() => new Map(teams.map((t) => [t.id, t.name])), [teams]);
  const members = useMemo(() => users.filter((u) => u.teamId === team.id), [users, team.id]);
  const candidates = useMemo(() => users.filter((u) => u.teamId !== team.id), [users, team.id]);

  async function handleAssign() {
    if (!selectedUserId) return;
    const user = users.find((u) => u.id === selectedUserId);
    if (user?.teamId) {
      const fromName = teamNameById.get(user.teamId) ?? "another team";
      const confirmed = window.confirm(
        `Move ${user.name} from ${fromName}? Stats attribute to the new team from now on.`,
      );
      if (!confirmed) return;
    }
    setBusy(true);
    try {
      await teamsApi.assignMember(team.id, selectedUserId);
      setSelectedUserId("");
      onChanged();
    } catch (e) {
      showApiError(e);
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(userId: string) {
    setBusy(true);
    try {
      await teamsApi.removeMember(userId);
      onChanged();
    } catch (e) {
      showApiError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.membersPanel}>
      <ul className={styles.memberList}>
        {members.length === 0 && <li className={styles.help}>No members yet.</li>}
        {members.map((u) => (
          <li key={u.id}>
            <span>{u.name}</span>
            <button type="button" disabled={busy} onClick={() => handleRemove(u.id)}>
              Remove
            </button>
          </li>
        ))}
      </ul>
      <div className={styles.addMemberRow}>
        <select
          aria-label="add member"
          value={selectedUserId}
          onChange={(e) => setSelectedUserId(e.target.value)}
        >
          <option value="">— select a user —</option>
          {candidates.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
              {u.teamId ? ` (currently on ${teamNameById.get(u.teamId) ?? "another team"})` : ""}
            </option>
          ))}
        </select>
        <button type="button" disabled={!selectedUserId || busy} onClick={handleAssign}>
          Add member
        </button>
      </div>
    </div>
  );
}
